<script lang="ts">
    import "../app.css";
    import "$lib/scss/global/_index.scss";
    import userJwt from "$stores/user-jwt";
    import {goto} from "$app/navigation";
    import {incrementProfileViewCount, profileViewCountStore} from "../stores/profile-view-counter";
    import {ALLOWED_ANONYMOUS_PROFILE_VIEWS} from "../lib/utilites/constants";
    import {SvelteToast} from "@zerodevx/svelte-toast";
    import {decodeUserJWTV2, isCountablePage} from "$lib/utilites/jwt";
    import {globalState} from "$stores/tab-store";
    import {onMount} from "svelte";
    import StripeModal from "$lib/components/stripe/StripeModal.svelte";
    import Spinner from "$lib/components/loading/Spinner.svelte";
    import ModalError from "$lib/components/error/ModalError.svelte";
    import userJWT from "../stores/user-jwt";
    import decode from "jwt-decode";
    import httpCommon from "../lib/services/http-common";
    import {page} from "$app/stores";
    import {UserRole} from "$types/types";

    export let data: {canonical_url: string; pathname: string};

    const instanceUrl = new URL($page.url);
    let user: App.UserJWT | null = null;

    let oldPathName: string; // ← variable to store the old value
    let loading = true;
    let isTailwindLoaded = false;

    $: if ($userJWT && $userJWT !== "LOADING") {
        let currentUser = decodeUserJWTV2($userJwt.token);

        if (!currentUser.rol.includes(UserRole.customer_company_admin) && !currentUser.rol.includes(UserRole.customer_company_user)) {
            if (data.pathname.includes("company")) {
                goto("/");
            }
        }
    }

    $: nameChanged(data.pathname);

    function nameChanged(newName: string) {
        if (newName !== "/account" && newName !== "/company" && newName !== "/account/reviews" && "/account/lists") {
            //logic for changing tabs
            //change this when unified dashboard
            localStorage.removeItem("TAB");
            globalState.set(null);
        }

        if (oldPathName == undefined || oldPathName !== newName) {
            // check value changed
            oldPathName = newName; // update the old value

            if (typeof window !== "undefined" && $userJwt !== "LOADING") {
                if ($userJwt.token === null || decodeUserJWTV2($userJwt.token).rol.includes(UserRole.anonymous_user)) {
                    if (isCountablePage(data.pathname)) {
                        incrementProfileViewCount();
                    }
                    // 2024-05-16 Anesu and Eric think that we should skip all this if the path includes "password",
                    // that is, always allow any user, anonymous or logged in to get to the password pages.
                    // Doing nothing about it now.

                    // 2024-05-16 If the user is trying to access the home page and they are over
                    // their profile view counts.
                    if (data.pathname === "/" && $profileViewCountStore.count > ALLOWED_ANONYMOUS_PROFILE_VIEWS) {
                        goto("/sign-up");
                    }

                    if (data.pathname.includes("d411id") && $profileViewCountStore.count > ALLOWED_ANONYMOUS_PROFILE_VIEWS) {
                        goto("/sign-up");
                    }

                    if (data.pathname.includes("company")) {
                        goto("/sign-up");
                    }

                    if (data.pathname.includes("account")) {
                        // will come back to this
                        if (data.pathname.includes("change-password")) {
                            goto("/sign-up");
                        }

                        /*if (data.pathname.includes('reset-password')) {
                            goto('/sign-in');
                        }*/

                        if (!data.pathname.includes("password")) {
                            if (!data.pathname.includes("confirm")) {
                                goto("/sign-up");
                            }
                        }
                    }
                }
            } else {
                data.pathname.includes("sign-in") && goto("/");
                data.pathname.includes("sign-up") && goto("/");
            }
        }
    }

    $: if (isTailwindLoaded) {
        setTimeout(() => {
            loading = false;
        }, 500);
    }

    onMount(() => {
        const checkTailwindLoaded = () => {
            const testElement = document.createElement("div");
            testElement.className = "bg-gray-100 text-black";
            document.body.appendChild(testElement);

            setTimeout(() => {
                const computedStyle = window.getComputedStyle(testElement);
                const bgColor = computedStyle.getPropertyValue("background-color");
                const textColor = computedStyle.getPropertyValue("color");
                // Verify if Tailwind styles are fully applied
                if (bgColor === "rgb(243, 244, 246)" && textColor === "rgb(0, 0, 0)") {
                    isTailwindLoaded = true;
                } else {
                    // Retry after a short delay if styles are not fully applied
                    setTimeout(checkTailwindLoaded, 50);
                }
                document.body.removeChild(testElement);
            }, 50);
        };

        checkTailwindLoaded();
    });

    onMount(() => {
        const unsubscribe = userJWT.subscribe(token => {
            if (typeof token === "object" && token.token) {
                user = decode(token.token);
            }
        });

        if (!instanceUrl.pathname.includes("welcome")) {
            if (typeof window !== "undefined" && $userJWT !== "LOADING") {
                if ($userJWT.token && $userJWT.token.length) {
                    httpCommon.defaults.headers.common.Authorization = $userJWT.token;
                    const token = `${$userJWT.token}`.replace(/bearer\s+/i, "");
                    user = decode(token);
                }
            }
        }

        return unsubscribe;
    });
</script>

<svelte:head>
    <link rel="canonical" href={data.canonical_url} />
    <link rel="preconnect" href={import.meta.env.VITE_API_HOSTNAME} />
    <meta property="og:locale" content="en_US" />
    {#if !data.canonical_url.includes("/the-411")}
        <meta property="og:type" content="website" />
    {/if}
    <meta property="og:url" content={data.canonical_url} />
    <meta property="og:site_name" content="Dock411" />
</svelte:head>

{#if loading || !user}
    <div style="display: flex; justify-content: center; align-items: center; height: 100vh; width: 100%;">
        <Spinner color="orange" />
    </div>
{:else}
    <div class="bg-[#F7F7F7]">
        <slot />
        {#if data.pathname.includes("company")}
            <StripeModal />
        {/if}
        <ModalError />
    </div>
    <SvelteToast options={{reversed: true, intro: {y: 192}, dismissable: false}} />
{/if}

<style>
  :root {
    --toastContainerTop: auto;
    --toastContainerRight: auto;
    --toastContainerBottom: 16px;
    --toastContainerLeft: 50%;
    --toastContainerTransform: translateX(-50%);
    --toastBackground: #2d3748; /* Gris oscuro azulado */
    --toastColor: #e2e8f0; /* Gris claro para el texto */
    --toastBarBackground: #a0aec0; /* Gris medio para la barra de progreso */
  }
</style>
